import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, Eye, Activity, ShoppingCart, AlertCircle, TrendingUp,
  ArrowUpRight, ArrowDownRight, Package, ChevronRight, Clock,
  RefreshCw, ExternalLink, Calendar,
} from "lucide-react";
import IndiaVisitorMap from "@/components/IndiaVisitorMap";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtCurrency = (n) => `₹${fmt(n)}`;

// ── Generate deterministic mock analytics ──────────────────────────────────
function genVisitorChart() {
  const days = [];
  const labels = ["01 May","04 May","07 May","10 May","13 May","16 May","19 May","22 May","26 May"];
  const base = [1200, 1800, 2200, 1700, 2600, 2900, 3100, 2800, 3500];
  return labels.map((d, i) => ({
    date: d,
    visitors: base[i] + Math.floor(Math.sin(i) * 300),
    pageViews: Math.floor((base[i] + Math.floor(Math.sin(i) * 300)) * 1.8),
  }));
}

const TRAFFIC_DATA = [
  { name: "Organic Search", value: 32.4, color: "#6366f1" },
  { name: "Direct",         value: 22.1, color: "#38bdf8" },
  { name: "Paid Search",    value: 18.6, color: "#34d399" },
  { name: "Social Media",   value: 12.3, color: "#fbbf24" },
  { name: "Referral",       value: 8.7,  color: "#f87171" },
  { name: "Others",         value: 5.9,  color: "#cbd5e1" },
];

const DEVICE_DATA = [
  { name: "Mobile",  value: 68.2, color: "#6366f1" },
  { name: "Desktop", value: 26.1, color: "#38bdf8" },
  { name: "Tablet",  value: 5.7,  color: "#34d399" },
];

const PAGES_PERF = [
  { page: "/ (Home)",       views: 12450, unique: 9820,  bounce: "32.4%" },
  { page: "/collections",   views: 8230,  unique: 6540,  bounce: "28.1%" },
  { page: "/products",      views: 7560,  unique: 5980,  bounce: "35.6%" },
  { page: "/cart",          views: 4210,  unique: 3120,  bounce: "48.2%" },
  { page: "/checkout",      views: 3980,  unique: 2850,  bounce: "40.7%" },
  { page: "/about",         views: 2360,  unique: 1880,  bounce: "42.1%" },
  { page: "/contact",       views: 1980,  unique: 1540,  bounce: "46.3%" },
];


const STATUS_COLORS = {
  placed:           "bg-blue-100 text-blue-700",
  accepted:         "bg-indigo-100 text-indigo-700",
  packed:           "bg-purple-100 text-purple-700",
  shipped:          "bg-amber-100 text-amber-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  delivered:        "bg-green-100 text-green-700",
  cancelled:        "bg-red-100 text-red-700",
  rejected:         "bg-red-100 text-red-700",
  paid:             "bg-green-100 text-green-700",
  pending:          "bg-yellow-100 text-yellow-700",
  failed:           "bg-red-100 text-red-700",
};

function StatusBadge({ status }) {
  const label = status?.replace(/_/g, " ");
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, change, color, sparkColor }) {
  const positive = change >= 0;
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5 truncate">{label}</p>
        <p className="text-2xl font-black text-slate-900 leading-tight">{value}</p>
        {change != null && <div className="flex items-center gap-1 mt-1">
          {positive
            ? <ArrowUpRight className="w-3 h-3 text-emerald-500" />
            : <ArrowDownRight className="w-3 h-3 text-red-500" />}
          <span className={`text-xs font-semibold ${positive ? "text-emerald-500" : "text-red-500"}`}>
            {positive ? "+" : ""}{change}%
          </span>
          <span className="text-xs text-slate-400">vs previous period</span>
        </div>}
      </div>
    </div>
  );
}

function SectionHeader({ title, link, linkLabel = "View All" }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      {link && (
        <Link to={link} className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5">
          {linkLabel} <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function Avatar({ name, color }) {
  const colors = ["bg-purple-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-cyan-500"];
  const bg = color || colors[name?.charCodeAt(0) % colors.length];
  return (
    <div className={`w-7 h-7 rounded-full ${bg} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

// ── Custom donut label ──────────────────────────────────────────────────────
function DonutLabel({ cx, cy, total, label }) {
  return (
    <g>
      <text x={cx} y={cy - 8} textAnchor="middle" className="text-base" style={{ fontSize: 18, fontWeight: 900, fill: "#0f172a" }}>
        {fmt(total)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontSize: 10, fill: "#64748b" }}>
        {label}
      </text>
    </g>
  );
}

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 Days" },
  { key: "30d",   label: "30 Days" },
  { key: "all",   label: "All Time" },
  { key: "custom",label: "Custom" },
];

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [period, setPeriod]       = useState("30d");
  const [customStart, setCustomStart] = useState(todayStr());
  const [customEnd,   setCustomEnd]   = useState(todayStr());
  const [showCustom, setShowCustom]   = useState(false);
  const [chartPeriod, setChartPeriod] = useState("Daily");

  const fetchData = useCallback((p, cs, ce) => {
    const token = localStorage.getItem("it_token");
    let url = `${API}/admin/dashboard?period=${p}`;
    if (p === "custom") url += `&start_date=${cs}&end_date=${ce}`;
    setLoading(true);
    axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(period, customStart, customEnd); }, []); // eslint-disable-line

  const handlePeriod = (p) => {
    setPeriod(p);
    setShowCustom(p === "custom");
    if (p !== "custom") fetchData(p, customStart, customEnd);
  };

  const applyCustom = () => fetchData("custom", customStart, customEnd);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const recentOrders = data?.recent_orders || [];
  const newUsers = data?.new_users || [];
  const topProducts = data?.top_products || [];
  const abandonedCarts = data?.abandoned_carts || [];
  const locations = data?.locations || [];
  const analytics = data?.analytics || {};
  const hasRealAnalytics = analytics.has_data;

  // Use real visitor chart data when available, else mock
  const visitorData = hasRealAnalytics && analytics.visitor_chart?.length > 0
    ? analytics.visitor_chart
    : genVisitorChart();

  // Real or mock device data
  const DEVICE_COLORS = { mobile: "#6366f1", desktop: "#38bdf8", tablet: "#34d399" };
  const deviceData = hasRealAnalytics && analytics.device_data?.length > 0
    ? analytics.device_data.map(d => ({ ...d, color: DEVICE_COLORS[d.name?.toLowerCase()] || "#94a3b8" }))
    : DEVICE_DATA;

  // Real or mock traffic data
  const TRAFFIC_COLORS = { "Organic Search": "#6366f1", "Direct": "#38bdf8", "Social Media": "#fbbf24", "Referral": "#34d399" };
  const trafficData = hasRealAnalytics && analytics.traffic_data?.length > 0
    ? analytics.traffic_data.map(d => ({ ...d, color: TRAFFIC_COLORS[d.name] || "#94a3b8" }))
    : TRAFFIC_DATA;

  // Real or mock page performance
  const pagePerfData = hasRealAnalytics && analytics.page_perf?.length > 0
    ? analytics.page_perf.map(p => ({ page: p.page, label: p.label || p.page, views: p.views, unique: p.unique }))
    : PAGES_PERF;

  const kpis = [
    { icon: Users,        label: "Total Users",      value: fmt(stats.total_users || 0),                                            color: "bg-indigo-50 text-indigo-600" },
    { icon: Eye,          label: "Page Views",        value: hasRealAnalytics ? fmt(stats.total_pageviews) : "—",                   color: "bg-blue-50 text-blue-600" },
    { icon: Activity,     label: "Sessions",          value: hasRealAnalytics ? fmt(stats.total_sessions) : "—",                    color: "bg-teal-50 text-teal-600" },
    { icon: ShoppingCart, label: "Revenue",           value: fmtCurrency(stats.revenue || 0),                                       color: "bg-amber-50 text-amber-600" },
    { icon: AlertCircle,  label: "Total Orders",      value: fmt(stats.total_orders || 0),                                           color: "bg-red-50 text-red-500" },
    { icon: TrendingUp,   label: "Total Products",    value: fmt(stats.total_products || 0),                                        color: "bg-violet-50 text-violet-600" },
  ];

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* ── Page Title + Date Filter ────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">Welcome back, here's what's happening with your store.</p>
          </div>
          <button
            onClick={() => fetchData(period, customStart, customEnd)}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-white border border-slate-200 px-3 py-1.5 rounded-lg"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => handlePeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                period === p.key
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-orange-300"
              }`}
            >{p.label}</button>
          ))}
        </div>

        {/* Custom date range picker */}
        {showCustom && (
          <div className="flex flex-wrap items-center gap-2 p-3 bg-white border border-orange-200 rounded-xl">
            <Calendar className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <span className="text-xs text-slate-500 font-medium">From</span>
            <input
              type="date"
              value={customStart}
              max={customEnd}
              onChange={e => setCustomStart(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-orange-400"
            />
            <span className="text-xs text-slate-500 font-medium">To</span>
            <input
              type="date"
              value={customEnd}
              min={customStart}
              max={todayStr()}
              onChange={e => setCustomEnd(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-orange-400"
            />
            <button
              onClick={applyCustom}
              className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600"
            >Apply</button>
          </div>
        )}
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </div>

      {/* ── Charts Row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Visitors & Page Views */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-slate-800">Visitors &amp; Page Views
              {!hasRealAnalytics && <span className="ml-2 text-[10px] font-normal text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Sample data — tracking active</span>}
            </h2>
            <select
              value={chartPeriod}
              onChange={e => setChartPeriod(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none bg-white text-slate-600"
            >
              {["Daily", "Weekly", "Monthly"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={visitorData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gPageViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Area type="monotone" dataKey="visitors"  stroke="#6366f1" strokeWidth={2} fill="url(#gVisitors)"  dot={false} name="Visitors" />
              <Area type="monotone" dataKey="pageViews" stroke="#38bdf8" strokeWidth={2} fill="url(#gPageViews)" dot={false} name="Page Views" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 justify-center">
            <span className="flex items-center gap-1 text-xs text-slate-500"><span className="w-3 h-0.5 bg-indigo-500 inline-block rounded" /> Visitors</span>
            <span className="flex items-center gap-1 text-xs text-slate-500"><span className="w-3 h-0.5 bg-sky-400 inline-block rounded" /> Page Views</span>
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Traffic Sources
            {!hasRealAnalytics && <span className="ml-2 text-[10px] font-normal text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Sample</span>}
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={trafficData} cx="50%" cy="50%" innerRadius={38} outerRadius={60}
                    dataKey="value" paddingAngle={2} startAngle={90} endAngle={-270}>
                    {trafficData.map((e, i) => <Cell key={i} fill={e.color || "#94a3b8"} />)}
                  </Pie>
                  <DonutLabel cx={65} cy={65} total={stats.total_sessions || 19460} label="Sessions" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {trafficData.map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || "#94a3b8" }} />
                    <span className="text-xs text-slate-600">{s.name}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-800">{s.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Device Breakdown
            {!hasRealAnalytics && <span className="ml-2 text-[10px] font-normal text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Sample</span>}
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={deviceData} cx="50%" cy="50%" innerRadius={38} outerRadius={60}
                    dataKey="value" paddingAngle={2} startAngle={90} endAngle={-270}>
                    {deviceData.map((e, i) => <Cell key={i} fill={e.color || "#94a3b8"} />)}
                  </Pie>
                  <DonutLabel cx={65} cy={65} total={stats.total_sessions || 19460} label="Sessions" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {deviceData.map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || "#94a3b8" }} />
                    <span className="text-xs text-slate-600">{s.name}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-800">{s.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tables Row 1 ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pages Performance */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title={`Pages Performance${!hasRealAnalytics ? " (Sample)" : ""}`} />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 pr-3 text-slate-400 font-semibold">Page</th>
                  <th className="text-right py-2 pr-3 text-slate-400 font-semibold">Views</th>
                  <th className="text-right py-2 text-slate-400 font-semibold">Unique</th>
                </tr>
              </thead>
              <tbody>
                {pagePerfData.slice(0, 7).map((p, i) => (
                  <tr key={i}
                    className={`border-b border-slate-50 hover:bg-slate-50 ${hasRealAnalytics ? "cursor-pointer" : ""}`}
                    onClick={() => hasRealAnalytics && window.open(p.page, "_blank", "noopener")}
                    title={p.page}>
                    <td className="py-2 pr-3 font-medium text-slate-700 truncate max-w-[160px]">
                      {hasRealAnalytics ? <span className="hover:text-blue-600 hover:underline">{p.label || p.page}</span> : (p.label || p.page)}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-600">{fmt(p.views)}</td>
                    <td className="py-2 text-right text-slate-600">{fmt(p.unique)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products by Views */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title="Top Products by Views" link="/admin/products" />
          <div className="space-y-2">
            {topProducts.length > 0 ? topProducts.map((p, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
                <div className="w-8 h-8 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {p.image
                    ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    : <Package className="w-4 h-4 text-slate-400 m-2" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate">{p.name}</p>
                  <p className="text-[11px] text-slate-400">{p.order_count} orders</p>
                </div>
                <Link to={p.id && p.id !== "None" ? `/admin/products/${p.id}` : "/admin/products"} className="text-blue-500 hover:text-blue-700 flex-shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            )) : (
              <p className="text-xs text-slate-400 text-center py-6">No orders yet</p>
            )}
          </div>
        </div>

        {/* Abandoned Carts */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title="Abandoned Carts" link="/admin/abandoned-cart" />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 pr-2 text-slate-400 font-semibold">Customer</th>
                  <th className="text-right py-2 pr-2 text-slate-400 font-semibold">Items</th>
                  <th className="text-right py-2 pr-2 text-slate-400 font-semibold">Value</th>
                  <th className="text-right py-2 text-slate-400 font-semibold">Last</th>
                </tr>
              </thead>
              <tbody>
                {abandonedCarts.map((c, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate("/admin/abandoned-cart")}>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1.5">
                        <Avatar name={c.customer_name || c.customer_email} />
                        <span className="truncate max-w-[80px] text-slate-700">{c.customer_email || c.customer_name || "—"}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-right text-slate-600">{c.items}</td>
                    <td className="py-2 pr-2 text-right font-semibold text-slate-800">{fmtCurrency(c.value)}</td>
                    <td className="py-2 text-right text-slate-400 whitespace-nowrap">{c.updated_at ? new Date(c.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</td>
                  </tr>
                ))}
                {abandonedCarts.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-400">No abandoned carts</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Tables Row 2 ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title="Recent Orders" link="/admin/orders" />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 pr-2 text-slate-400 font-semibold">Order</th>
                  <th className="text-left py-2 pr-2 text-slate-400 font-semibold">Customer</th>
                  <th className="text-right py-2 pr-2 text-slate-400 font-semibold">Amount</th>
                  <th className="text-right py-2 text-slate-400 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.slice(0, 5).map((o, i) => (
                  <tr key={i}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/orders?order=${o.id}`)}>
                    <td className="py-2 pr-2 font-mono font-semibold text-slate-700 truncate max-w-[90px]">
                      {o.order_number || o.id?.slice(0, 8).toUpperCase()}
                    </td>
                    <td className="py-2 pr-2 text-slate-600 truncate max-w-[80px]">{o.customer_name}</td>
                    <td className="py-2 pr-2 text-right font-bold text-slate-800">{fmtCurrency(o.amount)}</td>
                    <td className="py-2 text-right"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-400">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* New Users */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title="New Users" link="/admin/customers" linkLabel="View All" />
          <div className="space-y-2">
            {newUsers.length > 0 ? newUsers.map((u, i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-slate-50 last:border-0">
                <Avatar name={u.name} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{u.name}</p>
                  <p className="text-[11px] text-slate-400 truncate">{u.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="flex items-center gap-1 text-[11px] text-slate-400">
                    <Clock className="w-3 h-3" />
                    <span>{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</span>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-xs text-slate-400 text-center py-6">No users yet</p>
            )}
          </div>
        </div>

        {/* Visitors by Location — India bubble map */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <SectionHeader title="Visitors by Location" />
          <IndiaVisitorMap locations={locations} />
        </div>
      </div>
    </div>
  );
}
